import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { AddressAutofill } from '@mapbox/search-js-react';
import 'react-phone-input-2/lib/style.css';
import Flatpickr from 'react-flatpickr';
import PortalButton from '../components/Button';
import { postUserProfile } from '../api/me';
import { useAuth } from '../context/AuthContext';
const requiredFields = [
  'address_line_1',
  'city',
  'dob',
  'full_name',
  'phone_number',
  'postal_code',
  'state',
  'email',
];
const EditProfile = ({ setEdit }) => {
  const { user, setUser } = useAuth();
  const [profileData, setProfileData] = useState({
    address_line_1: '',
    address_line_2: '',
    city: '',
    dob: '',
    full_name: '',
    phone_number: '',
    postal_code: '',
    state: '',
    email: '',
  });
  const [errors, setErrors] = useState({});
  const handleChange = (field, value) => {
    if (field === 'phone_number') {
      // US phone number: 10 digits, only numbers allowed
      const cleaned = value.replace(/\D/g, '');
      let error = '';
      if (cleaned.length > 0 && cleaned.length !== 10) {
        error = 'Enter a valid 10-digit US phone number';
      }
      setProfileData((prev) => ({
        ...prev,
        [field]: value,
      }));
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else {
      setProfileData((prev) => ({
        ...prev,
        [field]: value,
      }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const addressChangeFn = (address) => {
    setProfileData((prev) => ({
      ...prev,
      address_line_1: address.features[0].properties.address_line1,
      address_line_2: address.features[0].properties.address_line2,
      city: address.features[0].properties.address_level2,
      state: address.features[0].properties.address_level1,
      postal_code: address.features[0].properties.postcode,
    }));
    setErrors((prev) => ({
      ...prev,
      address_line_1: '',
      city: '',
      state: '',
      postal_code: '',
    }));
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        address_line_1: user.address_line_1 || '',
        address_line_2: user.address_line_2 || '',
        city: user.city || '',
        dob: user.dob ? new Date(user.dob) : null,
        full_name: user.full_name || '',
        phone_number: user.phone_number || '',
        postal_code: user.postal_code || '',
        state: user.state || '',
        email: user.email || '',
      });
    }
  }, []);
  const onSaveProfile = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      const value = profileData[field];
      // Only trim if value is a string, otherwise just check for falsy
      if (typeof value === 'string') {
        if (!value.trim()) {
          newErrors[field] = 'This field is required';
        }
      } else if (!value) {
        newErrors[field] = 'This field is required';
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    const updatedProfile = {
      ...profileData,
      full_name: profileData?.full_name,
      phone_number: profileData?.phone_number,
      address_line_1: profileData?.address_line_1,
      address_line_2: profileData?.address_line_2,
      city: profileData?.city,
      state: profileData?.state,
      postal_code: profileData?.postal_code,
      dob: profileData?.dob ? new Date(profileData?.dob).toISOString() : '',
    };
    const response = postUserProfile(updatedProfile, user.id);
    response.then((data) => {
      console.log('data :>> ', data);
      if (data) {
        setUser(data);
        setEdit(false);
      }
    });
  };
  return (
    <div className="space-y-6">
      <div className="relative flex">
        <div className="w-full h-full flex flex-col after:flex-1">
          <div className="bg-white p-2 md:p-10 shadow-none md:shadow-lg">
            <div className="w-full mx-auto">
              <div className="space-y-8">
                <div>
                  <div className="grid gap-5 md:grid-cols-4 mb-5">
                    <div className="col-span-4 md:col-span-2 ">
                      <label
                        className="block text-sm font-medium mb-1 text-gray-400"
                        htmlFor="default">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={`w-full form-input bg-portal-100 font-medium text-portal-900 ${
                          errors?.full_name ? 'border border-red-500' : ''
                        }`}
                        value={profileData?.full_name || ''}
                        onChange={(e) =>
                          handleChange('full_name', e.target.value)
                        }
                      />
                      {errors?.full_name && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors?.full_name}
                        </div>
                      )}
                    </div>
                    <div className="col-span-4 md:col-span-2 relative">
                      <label
                        className="block text-sm font-medium mb-1 text-gray-400"
                        htmlFor="default">
                        DOB <span className="text-red-500">*</span>
                      </label>
                      <Flatpickr
                        value={profileData?.dob || ''}
                        onChange={([date]) => handleChange('dob', date)}
                        options={{
                          dateFormat: 'm/d/Y',
                          altInput: true,
                          altFormat: 'm/d/Y',
                          maxDate: 'today',
                        }}
                        className={`w-full form-input bg-portal-100 font-medium flatpickr-right text-portal-900 ${
                          errors?.dob ? 'border border-red-500' : ''
                        }`}
                        placeholder="MM/DD/YYYY"
                      />
                      {errors?.dob && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors?.dob}
                        </div>
                      )}
                    </div>
                    <div className="col-span-4 md:col-span-2 ">
                      <label className="block text-sm font-medium mb-1 text-gray-400">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`w-full form-input bg-portal-100 font-medium text-portal-900 ${
                          errors?.phone_number ? 'border border-red-500' : ''
                        }`}
                        value={profileData?.phone_number || ''}
                        onChange={(e) =>
                          handleChange('phone_number', e.target.value)
                        }
                      />
                      {errors?.phone_number && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors?.phone_number}
                        </div>
                      )}
                    </div>
                    <div className="col-span-4 md:col-span-2 ">
                      <label
                        className="block text-sm font-medium mb-1 text-gray-400"
                        htmlFor="default">
                        Email
                      </label>
                      <input
                        type="text"
                        value={profileData?.email || ''}
                        className={`w-full form-input bg-slate-200 disabled cursor-not-allowed font-medium text-portal-900`}
                        disabled
                        // onChange={(e) => handleChange('email', e.target.value)}
                      />
                      {errors?.email && (
                        <div className="text-red-500 text-xs mt-1">
                          {errors?.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-5 ">
                    <div className="col-span-1">
                      <AddressAutofill
                        accessToken="pk.eyJ1IjoiZ2lkZHlvcHMiLCJhIjoiY2w2Z3BhOTh1MDA4ZzNrbW1ya2N2cjhhOSJ9.gRglIHmbBz4nUr79eUgbJA"
                        options={{
                          language: 'en',
                          country: 'US',
                        }}
                        onRetrieve={addressChangeFn}>
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-400">
                              Street Address{' '}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="address-line1"
                              autoComplete="address-line1"
                              className={`w-full form-input bg-portal-100  font-medium text-portal-900 ${
                                errors?.address_line_1
                                  ? 'border border-red-500'
                                  : ''
                              }`}
                              value={profileData?.address_line_1 || ''}
                              onChange={(e) =>
                                handleChange('address_line_1', e.target.value)
                              }
                            />
                            {errors?.address_line_1 && (
                              <div className="text-red-500 text-xs mt-1">
                                {errors?.address_line_1}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-400">
                              Apt/Suite/Unit
                            </label>
                            <input
                              type="text"
                              name="address-line2"
                              autoComplete="address-line2"
                              className={`w-full form-input bg-portal-100  font-medium text-portal-900 ${
                                errors?.address_line_2
                                  ? 'border border-red-500'
                                  : ''
                              }`}
                              value={profileData?.address_line_2 || ''}
                              onChange={(e) =>
                                handleChange('address_line_2', e.target.value)
                              }
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1 text-gray-400">
                                City <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="address-level2"
                                autoComplete="address-level2"
                                className={`w-full form-input bg-portal-100  font-medium text-portal-900 ${
                                  errors?.city ? 'border border-red-500' : ''
                                }`}
                                value={profileData?.city || ''}
                                onChange={(e) =>
                                  handleChange('city', e.target.value)
                                }
                              />
                              {errors?.city && (
                                <div className="text-red-500 text-xs mt-1">
                                  {errors?.city}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1 text-gray-400">
                                State <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="address-level1"
                                autoComplete="address-level1"
                                className={`w-full form-input bg-portal-100  font-medium text-portal-900 ${
                                  errors?.state ? 'border border-red-500' : ''
                                }`}
                                value={profileData?.state || ''}
                                onChange={(e) =>
                                  handleChange('state', e.target.value)
                                }
                              />
                              {errors?.state && (
                                <div className="text-red-500 text-xs mt-1">
                                  {errors?.state}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1 text-gray-400">
                                Postal Code{' '}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="postal-code"
                                autoComplete="postal-code"
                                className={`w-full form-input bg-portal-100  font-medium text-portal-900 ${
                                  errors?.postal_code
                                    ? 'border border-red-500'
                                    : ''
                                }`}
                                value={profileData?.postal_code || ''}
                                onChange={(e) =>
                                  handleChange('postal_code', e.target.value)
                                }
                              />
                              {errors?.postal_code && (
                                <div className="text-red-500 text-xs mt-1">
                                  {errors?.postal_code}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </AddressAutofill>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-slate-700 mt-5 text-sm">
              By saving, you agree to our Privacy Policy and consent to the
              processing of your information for application purposes.
            </p>
            <div className="grid gap-1 md:grid-cols-2 my-5">
              <div className="pr-3">
                <PortalButton
                  classes="w-full"
                  variant="primary"
                  text="save"
                  onClick={() => onSaveProfile()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
